import React from "react";

import { useTranslation } from "utils";

import { Button, Flex, SelectableChoiceCard, Typography } from "components";

export type FootprintingAssessmentAutofillChoice = "autofill" | "no-autofill";

type FootprintingAssessmentAutofillProps = {
  year: number;
  itemsToAutofill: string[];
  selection: FootprintingAssessmentAutofillChoice;
  onChangeSelection: (choice: FootprintingAssessmentAutofillChoice) => void;
  onBack: () => void;
  onContinue: () => void;
};

export const FootprintingAssessmentAutofill = ({
  year,
  selection,
  itemsToAutofill,
  onChangeSelection,
  onBack,
  onContinue,
}: FootprintingAssessmentAutofillProps) => {
  const { t } = useTranslation("footprinting");

  return (
    <Flex className="assessment-selection" vertical align="center" gap={32}>
      <Flex className="assessment-selection-text" vertical gap={0} align="center">
        <Typography variant="paragraph-m" color="grey-600">
          {t("assessmentSelection.autofillChoice.callout")}
        </Typography>
        <Typography variant="h3">{t("assessmentSelection.autofillChoice.title")}</Typography>
      </Flex>
      <Flex className="assessment-selection-choice-cards" gap={16}>
        <SelectableChoiceCard
          className="assessment-selection-choice-card"
          icon={{ iconName: "tabler/copy", colour: "blue-light" }}
          tag={t("assessmentSelection.autofillChoice.saveTimeChip")}
          title={t("assessmentSelection.autofillChoice.reuseTitle", { year })}
          description={t("assessmentSelection.autofillChoice.reuseDescription")}
          bulletPoints={[
            <React.Fragment key="prefill">
              {t("assessmentSelection.autofillChoice.prefillLinePrefix")}{" "}
              <Typography variant="paragraph-s" bold>
                {itemsToAutofill.join(", ")}
              </Typography>
            </React.Fragment>,
            t("assessmentSelection.autofillChoice.changedLine", { year }),
            t("assessmentSelection.autofillChoice.reviewLine"),
          ]}
          selected={selection === "autofill"}
          onSelect={() => onChangeSelection("autofill")}
        />
        <SelectableChoiceCard
          className="assessment-selection-choice-card"
          icon={{ iconName: "tabler/pencil", colour: "beige" }}
          title={t("assessmentSelection.autofillChoice.startFromScratchTitle")}
          description={t("assessmentSelection.autofillChoice.startFromScratchDescription")}
          selected={selection === "no-autofill"}
          onSelect={() => onChangeSelection("no-autofill")}
        />
      </Flex>
      <Flex justify="space-between" className="assessment-selection-actions">
        <Button className="assessment-selection-back-button" size="small" type="secondary" onClick={onBack}>
          {t("assessmentSelection.backButton")}
        </Button>
        <Flex className="assessment-selection-page-indicator" align="center" gap={8}>
          <span className={`dot ${selection === "autofill" ? "active" : "inactive"}`} />
          <span className={`dot ${selection === "no-autofill" ? "active" : "inactive"}`} />
        </Flex>
        <Button className="assessment-selection-choice-continue-button" size="small" type="primary" onClick={onContinue}>
          {t("assessmentSelection.confirmChoiceButton")}
        </Button>
      </Flex>
    </Flex>
  );
};
